import React, { memo } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled
import styled from 'styled-components';
import { AnnouncementBanner } from '@helpcenter/announcement-banner';
import { isIncreasedFlexibilityEnabled } from '@helpcenter/feature-flags';
import { di } from 'react-magnetic-di';
import { useRouter } from 'react-resource-router';
import type { PortalAnnouncement } from 'rest/portal';
import type { State } from 'state';
import { getPortalId } from 'state/selectors/context';
import { getHelpCenterTopBarSplitFlag } from 'state/selectors/help-center';
import { getPortalAnnouncement, getPortal } from 'state/selectors/portal';
import { PaperContent } from 'view/layout/paper';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { Box } from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { GridContentWrapper } from '../grid-container/grid-container';
import type { GridAppearanceTypes } from '../grid-container/utils';

export interface StateProps {
	announcement: PortalAnnouncement;
	appearance?: GridAppearanceTypes;
	hasTopBarBeenSplit?: boolean;
	isPortalLoaded: boolean;
}

const Announcement: React.FC<StateProps> = ({
	announcement,
	hasTopBarBeenSplit,
	isPortalLoaded,
	appearance: contentAppearance,
}) => {
	di(useRouter);
	const [{ route }] = useRouter();
	const { name } = route;
	const whiteListedRouteNames = ['portal-home', 'request-create'];

	const AnnouncementInnerContainerDeprecated = memo(({ children }: { children: JSX.Element }) => {
		const hide = contentAppearance === 'none';
		const appearance = contentAppearance || 'normal';
		return (
			<GridContentWrapper appearance={appearance} hide={hide}>
				<PaperInnerContainer data-testid="paper-inner-container">{children}</PaperInnerContainer>
			</GridContentWrapper>
		);
	});

	AnnouncementInnerContainerDeprecated.displayName = 'AnnouncementInnerContainer';

	const AnnouncementInnerContainer = React.useMemo(() => {
		const Component = ({ children }: { children: JSX.Element }) => {
			const hide = contentAppearance === 'none';
			const appearance = contentAppearance || 'normal';
			return (
				<GridContentWrapper appearance={appearance} hide={hide}>
					<PaperInnerContainer data-testid="paper-inner-container">{children}</PaperInnerContainer>
				</GridContentWrapper>
			);
		};
		Component.displayName = 'AnnouncementInnerContainer';
		return Component;
	}, [contentAppearance]);

	if (isIncreasedFlexibilityEnabled() && hasTopBarBeenSplit) {
		return (
			<>
				{/* Show UFOHold when announcement data is loading on whitelisted pages */}
				{!isPortalLoaded && !announcement && whiteListedRouteNames.includes(name) && (
					<UFOLoadHold name="grid-announcement-banner-loading" />
				)}
				{!!announcement && whiteListedRouteNames.includes(name) && (
					<Box xcss={announcementContainer} testId={'increased-flexibility-container'}>
						<AnnouncementBanner
							actionSubjectId="portalAnnouncementBanner"
							header={announcement.userLanguageHeader}
							messageHtml={announcement.userLanguageMessageWiki}
							InnerContainerComponent={
								FeatureGates.checkGate('help_center_ttvc_hydration_resolve')
									? AnnouncementInnerContainer
									: AnnouncementInnerContainerDeprecated
							}
						/>
					</Box>
				)}
			</>
		);
	}

	return (
		<>
			{/* Show UFOHold when announcement data is loading on whitelisted pages */}
			{!isPortalLoaded && !announcement && whiteListedRouteNames.includes(name) && (
				<UFOLoadHold name="grid-announcement-banner-loading" />
			)}
			{!!announcement && whiteListedRouteNames.includes(name) && (
				<AnnouncementBanner
					actionSubjectId="portalAnnouncementBanner"
					header={announcement.userLanguageHeader}
					messageHtml={announcement.userLanguageMessageWiki}
					InnerContainerComponent={
						FeatureGates.checkGate('help_center_ttvc_hydration_resolve')
							? AnnouncementInnerContainer
							: AnnouncementInnerContainerDeprecated
					}
				/>
			)}
		</>
	);
};

const selector = createSelector(
	(state: State) => {
		const portalId = getPortalId(state);
		const portal = getPortal(state, portalId);
		const announcement = getPortalAnnouncement(state, portalId);

		return {
			announcement,
			isPortalLoaded: Boolean(portal),
		};
	},
	getHelpCenterTopBarSplitFlag,
	({ announcement, isPortalLoaded }, hasTopBarBeenSplit) => {
		return {
			announcement,
			hasTopBarBeenSplit,
			isPortalLoaded,
		};
	},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const PaperInnerContainer = styled(PaperContent)({
	paddingTop: 0,
});

const announcementContainer = xcss({
	marginTop: 'space.100',
	marginBottom: 'space.100',
	marginLeft: 'space.400',
	marginRight: 'space.400',
	borderRadius: 'radius.large',
	overflow: 'hidden',
	// make sure extra margin is not added in case announcement is not present
	':empty': {
		display: 'none',
	},
});

export default connect(selector)(Announcement);
